/**
 * Empty / error states for the order tracking page:
 *  - "no active order" (when there is no current order to track)
 *  - "payment failed after Stripe 3DS redirect"
 */

import { AlertCircle, ArrowLeft, Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardContent,
  ThemeSwitcher,
} from '@/components/ui'

interface NoActiveOrderStateProps {
  onBackToMenu: () => void
}

export function NoActiveOrderState({ onBackToMenu }: NoActiveOrderStateProps) {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={onBackToMenu}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('common:backToMenu')}
          </Button>
          <ThemeSwitcher />
        </div>

        <Card className="text-center py-12">
          <CardContent>
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('common:noActiveOrder')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('common:noOrdersToTrack')}
            </p>
            <Button variant="black" onClick={onBackToMenu}>
              {t('common:startOrdering')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

interface PaymentFailedStateProps {
  message: string
  onBackToCheckout: () => void
}

export function PaymentFailedState({
  message,
  onBackToCheckout,
}: PaymentFailedStateProps) {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={onBackToCheckout}>
            <ArrowLeft className="h-4 w-4 mr-1" /> {t('common:backToCheckout')}
          </Button>
        </div>

        <Card className="text-center py-12">
          <CardContent>
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('common:paymentFailed')}</h2>
            <p className="text-muted-foreground mb-6">{message}</p>
            <Button variant="black" onClick={onBackToCheckout}>
              {t('common:tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
